/* eslint-disable react/display-name */
import React, { memo, useState } from 'react';

import expandArrow from './assets/ic_expand_arrow.svg';
import collapseArrow from './assets/ic_collapse_arrow.svg';
import windImage from './assets/hurricane/ic_wind.svg';
import distanceImage from './assets/hurricane/ic_distance.svg';
import coordinatesImage from './assets/hurricane/ic_coordinates.svg';
import pressureImage from './assets/hurricane/ic_air_pressure.svg';
import stormGustImage from './assets/hurricane/ic_storm_gust.svg';
import hurricaneIcons from './assets/hurricane/index';
import { utilities } from '@weatherbug/common';
import styled from 'styled-components';
import { HurricaneType } from '../utilities/interfaces';
import { convertUnixTimeStampToDate } from '../utilities/dateTimeUtils';

const hurricaneUtil = new utilities.HurricaneUtil();
const dateFormat = 'MMM. d, hh a';

interface HurricaneInfoProps {
    hurricane: HurricaneType;
    showExpanded: boolean;
}

const getImageSrc = (hurricane: HurricaneType): string | undefined => {
    if (!hurricane) {
        return undefined;
    }
    const currentDType = hurricane?.dtype;
    const currentT = hurricane?.t;
    let imageSrc = 'dialog-ic';
    if (currentDType === hurricaneUtil.pastDtype) {
        imageSrc += '-past';
    }

    if (currentT === hurricaneUtil?.hurricaneType) {
        imageSrc += hurricane?.categoryNumber;
    } else if (hurricaneUtil?.tropicalStormTypes?.includes(currentT)) {
        imageSrc += 'TS';
    } else if (hurricaneUtil?.tropicalDepressionTypes?.includes(currentT)) {
        imageSrc += 'TD';
    }

    return imageSrc;
};

export const HurricaneInfo = memo(({
    hurricane,
    showExpanded = false
}: HurricaneInfoProps) => {
    const [ expanded, setExpanded] = useState(showExpanded);

    const imageSrc = getImageSrc(hurricane);

    const toggleExpansion = () => {
        setExpanded(!expanded);
    };

    const renderCondensed = () => {
        return <ExpandButton onClick={toggleExpansion}>
            <img alt='Expand Hurricane info' src={expandArrow} />
        </ExpandButton>;
    };

    const renderExpanded = (hurricane: HurricaneType, showExpanded: boolean) => { 
        return (
            <div>
                <InfoContentContainer>
                    <InfoSection>
                        <Image alt='Hurricane coordinates info' src={coordinatesImage} />
                        <LeftContainer>
                            <div>{hurricane.coordinates}</div>
                            <Label>Coordinates</Label>
                        </LeftContainer>
                    </InfoSection>
                    <InfoSection>
                        <Image alt='Hurricane pressure' src={pressureImage} />
                        <div>
                            { 
                                !hurricane.pressure
                                    ? <NoData>--</NoData>
                                    : <div>{`${hurricane.pressure} ${hurricane.pressureUnit}`}</div>
                            }
                            <Label>Pressure</Label>
                        </div>
                    </InfoSection>
                </InfoContentContainer>
                <InfoContentContainer>
                    <InfoSection>
                        <Image alt='Hurricane direction and speed' src={windImage} />
                        <LeftContainer>
                            {!hurricane.direction && !hurricane.speed
                                ? <NoData>--</NoData>
                                : <div>
                                    {`${hurricane.direction || '--'} `}
                                    <SmallDot>&#8226;</SmallDot> {` ${hurricane.speed || '--'} ${hurricane.speedUnit}`}
                                </div>
                            }
                            <Label>Direction</Label>
                        </LeftContainer>
                    </InfoSection>
                </InfoContentContainer>
                { !showExpanded && <ExpandButton onClick={toggleExpansion}>
                    <img alt='Collapse hurricane info' src={collapseArrow} />
                </ExpandButton>
                } 
            </div>
        );
    }; 

    const timestampInMilliseconds = hurricane?.datetimeUtc  ? hurricane.datetimeUtc * 1000 : null;
    const hurricaneFormattedTime = Number.isFinite(timestampInMilliseconds) 
        ? convertUnixTimeStampToDate(timestampInMilliseconds!, dateFormat) 
        : '--';   

    return <Container showExpanded={showExpanded}>
        {hurricane && <InfoContainer>
            <Header>
                <div>
                    <HeaderImage alt='Hurricane category' src={hurricaneIcons[imageSrc!]} />
                </div>
                <div>
                    {hurricane.category && hurricane.category.toUpperCase()}, {hurricane.basin}<br />
                    {hurricane.pointType}, {hurricaneFormattedTime}
                </div>
            </Header>
            <InfoContentContainer>
                <InfoSection>
                    <Image alt='Hurricane storm gust' src={stormGustImage} />
                    <LeftContainer>
                        <div>{`${hurricane.wind} | ${hurricane.gust} ${hurricane.windUnit}`}</div>
                        <Label>Wind | Gust</Label>
                    </LeftContainer>
                </InfoSection>
                <InfoSection>
                    <Image alt='Hurricane distance' src={distanceImage} />
                    <div>
                        <div>{`${hurricane.distance} ${hurricane.distanceUnit}`}</div>
                        <Label>From your Location</Label>
                    </div>
                </InfoSection>
            </InfoContentContainer>
            {expanded
                ? renderExpanded(hurricane, showExpanded)
                : renderCondensed()
            }
        </InfoContainer>}
    </Container>;
});

interface ContainerProps {
    showExpanded: boolean;
}
const Container = styled.div<ContainerProps>`
    display: flex;
    color: #666666;
    font-weight: 400;
    border-radius: 0px 0px 2px 2px;
    box-shadow: ${({ showExpanded }) => showExpanded ? 'none' : '0 2px 4px 0 rgba(0, 0, 0, 0.5)'};
    background-color: ${({ showExpanded }) => showExpanded ? '#F9F9F9' : '#FFFFFF'};
`;

const InfoContainer = styled.div`
    width: 100%;
`;

const Header = styled.div`
    border-bottom: 1px solid #BCBBC1;
    padding-top: 8px;
    padding-bottom: 8px;
    padding-left: 10px;
    font-size: 14px;
    display: flex;
`;

const InfoContentContainer = styled.div`
    display: flex;
    padding-top: 8px;
`;

const InfoSection = styled.div`
    width: 50%;
    display: flex;
    font-size: 14px;
`;

const LeftContainer = styled.div`
    margin-bottom: 4px;
`;

const ExpandButton = styled.div`
    cursor: pointer;
    display: flex;
    height: 24px;
    align-items: center;
    justify-content: center;
    border-top: 1px solid #BCBBC1;
    width: 100%;
    margin-top: 4px;
`;

const SmallDot = styled.span`
    font-size: 13px;
`;

const Label = styled.div`
    font-size: 13px;
    color: #8E8E8E;
`;

const NoData = styled.div`
    font-size: 18px;
    line-height: 16px;
`;

const HeaderImage = styled.img`
    height: 24px;
    margin-right: 8px;
`;

const Image = styled.img`
    padding-left: 8px;
    padding-right: 8px;
    width: 39px;
    height: 39px;
`;
